import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Smartphone, Users, Zap, Lock, RefreshCw, BarChart } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useState } from "react";
import ContactFormModal from "@/components/ContactFormModal";

export default function MobileApps() {
  const [isContactOpen, setIsContactOpen] = useState(false);

  const features = [
    {
      icon: Smartphone,
      title: "Cross-Platform",
      description: "One codebase for both iOS and Android, reducing development time and cost"
    },
    {
      icon: Users,
      title: "User-Centric Design",
      description: "Intuitive interfaces that users love, designed with your audience in mind"
    },
    {
      icon: Zap,
      title: "High Performance",
      description: "Native-level performance with smooth animations and fast load times"
    },
    {
      icon: Lock,
      title: "Secure by Default",
      description: "Industry-standard security practices to protect your users' data"
    },
    {
      icon: RefreshCw,
      title: "Easy Updates",
      description: "Push updates instantly without app store delays"
    },
    {
      icon: BarChart,
      title: "Analytics Built-In",
      description: "Track user behavior and make data-driven improvements"
    },
  ];

  const platforms = [
    { name: "iOS", description: "Native experience for iPhone and iPad users" },
    { name: "Android", description: "Reach billions of Android device users worldwide" },
    { name: "Progressive Web App", description: "Works everywhere, installs like a native app" },
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-secondary/20 via-primary/20 to-background" />
          
          <div className="container relative z-10 max-w-4xl mx-auto text-center">
            <motion.h1
              className="text-5xl md:text-6xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="text-gradient">Mobile Apps</span> That Scale
            </motion.h1>
            
            <motion.p
              className="text-xl text-muted-foreground mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Put your business in your customers' pockets with a custom mobile app built for growth.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Button
                size="lg"
                onClick={() => setIsContactOpen(true)}
                className="bg-gradient-to-r from-secondary to-primary text-white"
              >
                Build Your App
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 px-4">
          <div className="container max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Everything You Need in a Mobile App
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  className="p-6 rounded-lg bg-card border border-border hover:border-secondary/50 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <feature.icon className="w-12 h-12 text-secondary mb-4" />
                  <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Platform Options */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="container max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Choose Your Platform
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {platforms.map((platform, index) => (
                <motion.div
                  key={platform.name}
                  className="p-6 rounded-lg bg-card border border-border text-center"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <h3 className="text-2xl font-bold mb-3">{platform.name}</h3>
                  <p className="text-muted-foreground">{platform.description}</p>
                </motion.div>
              ))}
            </div>
            
            <p className="text-center text-muted-foreground mt-8">
              Not sure which platform? We'll help you choose the best option for your audience and budget.
            </p>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-20 px-4">
          <div className="container max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Perfect For
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {[
                "Customer loyalty programs",
                "Field service management",
                "E-commerce and shopping",
                "Booking and scheduling",
                "Internal team tools",
                "Social and community platforms"
              ].map((useCase, index) => (
                <motion.div
                  key={useCase}
                  className="flex items-center gap-3 p-4 rounded-lg bg-card border border-border"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-secondary to-primary" />
                  <span className="text-lg">{useCase}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4">
          <div className="container max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Let's Build Your Mobile App
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              From concept to launch, we'll guide you through every step of the process.
            </p>
            <Button
              size="lg"
              onClick={() => setIsContactOpen(true)}
              className="bg-gradient-to-r from-secondary to-primary text-white"
            >
              Start Your Project
            </Button>
          </div>
        </section>
      </div>
      <Footer />
      <ContactFormModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </>
  );
}
